import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

interface CreditUsageTrendTooltipProps {
  active?: boolean
  payload?: Array<{ payload?: { date?: string; value?: number } }>
  label?: string
  canDrillIntoRecords: boolean
  partialDate?: string
  onDateClick?: (date: string) => void
}

const CreditUsageTrendTooltip = ({
  active,
  payload,
  label,
  canDrillIntoRecords,
  partialDate,
  onDateClick,
}: CreditUsageTrendTooltipProps) => {
  const { t } = useTranslation()
  const point = payload?.[0]?.payload
  const pointDate = point?.date
  const pointValue = point?.value ?? 0

  if (!active || !pointDate) return null

  const content = (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg">
      <div className="font-medium">{pointDate || label}</div>
      <div>
        {t("billing.plan.creditUsageRecords.tooltipValue", {
          value: formatCredits(pointValue),
        })}
      </div>
      {pointDate === partialDate && (
        <div className="mt-1 text-xs text-muted-foreground">
          {t("billing.plan.creditUsageRecords.partialToday")}
        </div>
      )}
      {canDrillIntoRecords && (
        <div className="mt-1 text-xs text-muted-foreground">
          {t("billing.plan.creditUsageRecords.dailyHint")}
        </div>
      )}
    </div>
  )

  if (!canDrillIntoRecords || !onDateClick) return content

  return (
    <button
      type="button"
      className="cursor-pointer text-left"
      onClick={() => onDateClick(pointDate)}
    >
      {content}
    </button>
  )
}

export interface CreditUsageTrendChartPoint {
  label: string
  value: number
  date: string
}

export interface CreditUsageTrendChartCardProps {
  title?: string
  subtitle?: string
  details?: string
  headerAction?: React.ReactNode
  data: CreditUsageTrendChartPoint[]
  isLoading: boolean
  emptyLabel: string
  formatValue?: (value: number) => string
  yAxisWidth?: number
  canDrillIntoRecords?: boolean
  /**
   * ISO date of the in-progress day. When it matches the last data point,
   * the final segment renders dashed so partial totals don't read as a drop.
   */
  partialDate?: string
  onDateClick?: (date: string) => void
  embedded?: boolean
  compact?: boolean
}

const CreditUsageTrendChartCard = ({
  title,
  subtitle,
  details,
  headerAction,
  data,
  isLoading,
  emptyLabel,
  formatValue = formatCredits,
  yAxisWidth = 42,
  canDrillIntoRecords = false,
  partialDate,
  onDateClick,
  embedded = false,
  compact = false,
}: CreditUsageTrendChartCardProps) => {
  const { t } = useTranslation()

  const lastIndex = data.length - 1
  const hasPartialPoint =
    Boolean(partialDate) &&
    lastIndex >= 0 &&
    data[lastIndex].date === partialDate
  // Original value/date fields stay intact for the tooltip and drilldown;
  // derived keys split the line into a solid run and a dashed today segment.
  const chartData = data.map((point, index) => ({
    ...point,
    solidValue: hasPartialPoint && index === lastIndex ? null : point.value,
    partialValue:
      hasPartialPoint && index >= lastIndex - 1 ? point.value : null,
  }))

  const chartContent = (
    <CardContent
      className={cn(
        "relative",
        embedded
          ? "h-full px-0 pb-0"
          : compact
            ? "h-[128px] px-4 pb-4 lg:h-[132px] lg:px-4 lg:py-0 lg:pr-5"
            : "h-[260px] px-4 pb-6",
        canDrillIntoRecords && "cursor-pointer"
      )}
    >
      {!isLoading && canDrillIntoRecords && onDateClick && data.length > 0 ? (
        <select
          value=""
          className="absolute right-2 top-2 z-10 rounded-md bg-background px-2 py-1 text-xs font-medium text-foreground opacity-0 shadow-sm ring-1 ring-border transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("billing.plan.creditUsageRecords.keyboardAction")}
          onChange={(event) => {
            if (event.target.value) onDateClick(event.target.value)
          }}
        >
          <option value="" disabled>
            {t("billing.plan.creditUsageRecords.keyboardAction")}
          </option>
          {data.map((point) => (
            <option key={point.date} value={point.date}>
              {point.date}
            </option>
          ))}
        </select>
      ) : null}
      {isLoading ? (
        <Skeleton className="h-full w-full" />
      ) : data.length === 0 ? (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ left: 0, right: 8, top: 10 }}
            onClick={(state) => {
              const pointDate = state?.activePayload?.[0]?.payload?.date
              if (pointDate && canDrillIntoRecords && onDateClick) {
                onDateClick(pointDate)
              }
            }}
            className={canDrillIntoRecords ? "cursor-pointer" : undefined}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              width={yAxisWidth}
              tickFormatter={formatValue}
            />
            <RechartsTooltip
              content={
                <CreditUsageTrendTooltip
                  canDrillIntoRecords={canDrillIntoRecords}
                  partialDate={hasPartialPoint ? partialDate : undefined}
                  onDateClick={onDateClick}
                />
              }
            />
            <Line
              type="monotone"
              dataKey="solidValue"
              stroke="#0F766E"
              strokeWidth={compact ? 2 : 2.5}
              dot={{ r: compact ? 2 : 2.5 }}
              activeDot={{ r: compact ? 3.5 : 4 }}
            />
            {hasPartialPoint && (
              <Line
                type="monotone"
                dataKey="partialValue"
                stroke="#0F766E"
                strokeWidth={compact ? 2 : 2.5}
                strokeDasharray="4 4"
                dot={{ r: compact ? 2 : 2.5 }}
                activeDot={{ r: compact ? 3.5 : 4 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  )

  if (embedded) {
    return chartContent
  }

  return (
    <Card
      className={cn(
        !compact && "h-full",
        compact &&
          "gap-0 py-4 shadow-sm lg:grid lg:grid-cols-[minmax(260px,360px)_minmax(0,1fr)] lg:items-center"
      )}
    >
      <CardHeader
        className={cn("gap-3", compact && "gap-2 px-5 pb-3 lg:py-0 lg:pr-3")}
      >
        <div
          className={cn(
            "flex items-start justify-between gap-3",
            compact && "flex-col"
          )}
        >
          <div className="space-y-1">
            {title ? (
              <CardTitle
                className={cn(
                  "font-semibold text-foreground",
                  compact ? "text-sm" : "text-base"
                )}
              >
                {title}
              </CardTitle>
            ) : null}
            {subtitle ? (
              <p
                className={cn(
                  "text-muted-foreground",
                  compact ? "text-xs leading-5" : "text-sm"
                )}
              >
                {subtitle}
              </p>
            ) : null}
            {details ? (
              <p
                className={cn(
                  "text-muted-foreground",
                  compact ? "text-xs leading-5" : "text-sm"
                )}
              >
                {details}
              </p>
            ) : null}
            {canDrillIntoRecords && data.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("billing.plan.creditUsageRecords.dailyHint")}
              </p>
            ) : null}
          </div>
          {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
        </div>
      </CardHeader>
      {chartContent}
    </Card>
  )
}

export default CreditUsageTrendChartCard
